// Pure helpers for the Transcripts dashboard table.

export const PAGE_SIZE = 25;

export function displayName(person) {
  return person?.name || person?.email || 'Unknown User';
}

// Blank-named transcripts display as "Untitled" and must sort/search as such
// (under 'U'), not as an empty string that floats to the top.
export function transcriptDisplayName(row) {
  return row?.name || 'Untitled';
}

export function isAssignedTo(row, userId) {
  if (!userId) return false;
  return (row.assignees || []).some(a => String(a.user_id) === String(userId));
}

// The sortable "progress" metric is the coded-snippet count — cheap to compute
// for every transcript, so sort/filter never need the (lazily-fetched)
// coverage bars. See docs/transcript-dashboard-performance.md.
export function rowProgress(row) {
  return row.snippet_count || 0;
}

// Recency for the "Last updated" column: the most recent of when the transcript
// was last coded and its own updated_at (a rename or body edit is a change too).
// updated_at is always present, so this is never null — uncoded transcripts rank
// by their edit time rather than sinking to the bottom.
export function rowRecency(row) {
  const coded = row.last_coded_at ? new Date(row.last_coded_at).getTime() : 0;
  const updated = row.updated_at ? new Date(row.updated_at).getTime() : 0;
  return Math.max(coded, updated);
}

/**
 * Collapse a 0/1 bucket array into runs for rendering segmented bars.
 * @param {Array<number>} buckets
 * @returns {Array<{coded: boolean, count: number}>}
 */
export function bucketsToRuns(buckets) {
  const runs = [];
  (buckets || []).forEach(bucket => {
    const coded = !!bucket;
    if (runs.length > 0 && runs[runs.length - 1].coded === coded) {
      runs[runs.length - 1].count++;
    } else {
      runs.push({ coded, count: 1 });
    }
  });
  return runs;
}

/**
 * Sort dashboard rows by a column. 'progress' sorts by coded-snippet count and
 * 'updated' by rowRecency (max of last_coded_at and updated_at) — both plain
 * numeric sorts, so the lowest values (zero snippets / oldest) lead ascending
 * and trail descending rather than being pinned to the bottom.
 * @param {Array} rows - dashboard transcript rows
 * @param {string|null} sortCol - 'name' | 'assigned' | 'progress' | 'updated'
 * @param {string} sortDir - 'asc' | 'desc'
 */
export function sortRows(rows, sortCol, sortDir) {
  if (!sortCol) return rows;
  const dir = sortDir === 'desc' ? -1 : 1;

  return [...rows].sort((a, b) => {
    if (sortCol === 'progress') {
      return (rowProgress(a) - rowProgress(b)) * dir;
    }

    if (sortCol === 'updated') {
      return (rowRecency(a) - rowRecency(b)) * dir;
    }

    if (sortCol === 'assigned') {
      const countA = (a.assignees || []).length;
      const countB = (b.assignees || []).length;
      if (countA !== countB) return (countA - countB) * dir;
      const firstA = (a.assignees || [])[0];
      const firstB = (b.assignees || [])[0];
      const nameA = firstA ? displayName(firstA) : '';
      const nameB = firstB ? displayName(firstB) : '';
      return nameA.localeCompare(nameB) * dir;
    }

    return transcriptDisplayName(a).localeCompare(transcriptDisplayName(b)) * dir;
  });
}

/**
 * Filter dashboard rows by the single active filter plus the name search.
 * @param {Array} rows - dashboard transcript rows
 * @param {Object} filters - { activeFilter, searchQuery }
 *   activeFilter: null | 'assigned_to_me' | 'unassigned' | 'not_started'
 * @param {string|number} currentUserId
 */
export function filterRows(rows, { activeFilter, searchQuery }, currentUserId) {
  const query = (searchQuery || '').trim().toLowerCase();

  return rows.filter(row => {
    if (query && !transcriptDisplayName(row).toLowerCase().includes(query)) return false;
    if (activeFilter === 'assigned_to_me' && !isAssignedTo(row, currentUserId)) return false;
    if (activeFilter === 'unassigned' && (row.assignees || []).length > 0) return false;
    if (activeFilter === 'not_started' && rowProgress(row) > 0) return false;
    return true;
  });
}
